/**
 * DTI v3 Public API Branded String Type Schemas
 *
 * This file is the SINGLE SOURCE OF TRUTH for all Zod schemas used by the DTI v3 public API.
 * Every branded type is derived from its corresponding Zod schema via z.infer<typeof schema>.
 *
 * Schema Organization:
 * - Tier 1 (Structural): Pattern-based validation (regex, URLs, ISO timestamps)
 * - Tier 2 (Enum): True GraphQL enums or intentional closed vocabularies backed by CHAS models/specs
 * - Tier 3 (Identity): Non-empty trimmed strings for IDs, labels, tracking fields
 *
 * Important:
 * - CHAS public GraphQL files (chas/app/graphql/public/) are the source of truth for nullability and field names
 * - parseBranded(schema, value): Maybe<T> — use for optional-result contexts (components, hooks)
 * - unsafeParse(schema, value): T — use for required-result contexts (non-optional operations)
 * - DO NOT use "as BrandedType" casts. Use schemas + z.infer instead.
 * - Tier 2 enums require either a CHAS GraphQL enum or documented deliberate decision to close the vocabulary
 */

import z from 'zod';

import type {
  Email,
  FirstName,
  InsuranceCompanyId,
  InsuranceSearchCode,
  IsoDate,
  IsoTimestamp,
  LastName,
  LeadId,
  MaskedEmail,
  MaskedPhone,
  NormalizedStateCode,
  ProviderId,
  UsPhone,
  UsStateCode,
  ZipCode,
} from '@/core/dtiPublicApi/types';
import {
  brandNormalizedStateCode,
  brandUsStateCode,
  isReservedInsuranceSelectionValue,
  NORMALIZED_STATE_CODE_VALUES,
  SPECIALTY_CODE_VALUES,
  SUBSCRIBER_RELATIONSHIPS,
  US_STATE_CODE_VALUES,
} from '@/core/dtiPublicApi/types';
import { just, type Maybe, nothing } from '@/core/maybe';

// ============================================================================
// BRANDED TYPE SYMBOLS & DECLARATIONS
// ============================================================================

declare const appointmentIdBrand: unique symbol;
export type AppointmentId = string & {
  readonly [appointmentIdBrand]: 'AppointmentId';
};

declare const memberIdBrand: unique symbol;
export type MemberId = string & { readonly [memberIdBrand]: 'MemberId' };

declare const groupIdBrand: unique symbol;
export type GroupId = string & { readonly [groupIdBrand]: 'GroupId' };

declare const reasonForVisitCodeBrand: unique symbol;
export type ReasonForVisitCode = string & {
  readonly [reasonForVisitCodeBrand]: 'ReasonForVisitCode';
};

declare const stripeTokenBrand: unique symbol;
export type StripeToken = string & {
  readonly [stripeTokenBrand]: 'StripeToken';
};

declare const magicLinkTokenBrand: unique symbol;
export type MagicLinkToken = string & {
  readonly [magicLinkTokenBrand]: 'MagicLinkToken';
};
declare const leadInviteTokenBrand: unique symbol;
export type LeadInviteToken = string & {
  readonly [leadInviteTokenBrand]: 'LeadInviteToken';
};

declare const dtiSessionTokenBrand: unique symbol;
export type DtiSessionToken = string & {
  readonly [dtiSessionTokenBrand]: 'DtiSessionToken';
};

declare const membershipPackageIdBrand: unique symbol;
export type MembershipPackageId = string & {
  readonly [membershipPackageIdBrand]: 'MembershipPackageId';
};

declare const membershipPackageNameBrand: unique symbol;
export type MembershipPackageName = string & {
  readonly [membershipPackageNameBrand]: 'MembershipPackageName';
};

declare const membershipPackageDescriptionBrand: unique symbol;
export type MembershipPackageDescription = string & {
  readonly [membershipPackageDescriptionBrand]: 'MembershipPackageDescription';
};

declare const membershipIntervalBrand: unique symbol;
export type MembershipInterval = string & {
  readonly [membershipIntervalBrand]: 'MembershipInterval';
};

declare const partnerNameBrand: unique symbol;
export type PartnerName = string & {
  readonly [partnerNameBrand]: 'PartnerName';
};

declare const dtiConfigConditionFieldBrand: unique symbol;
export type DtiConfigConditionField = string & {
  readonly [dtiConfigConditionFieldBrand]: 'DtiConfigConditionField';
};

declare const dtiConfigConditionValueBrand: unique symbol;
export type DtiConfigConditionValue = string & {
  readonly [dtiConfigConditionValueBrand]: 'DtiConfigConditionValue';
};

declare const dtiConfigActionNameBrand: unique symbol;
export type DtiConfigActionName = string & {
  readonly [dtiConfigActionNameBrand]: 'DtiConfigActionName';
};

declare const dtiConfigFieldNameBrand: unique symbol;
export type DtiConfigFieldName = string & {
  readonly [dtiConfigFieldNameBrand]: 'DtiConfigFieldName';
};

declare const dtiConfigStepNameBrand: unique symbol;
export type DtiConfigStepName = string & {
  readonly [dtiConfigStepNameBrand]: 'DtiConfigStepName';
};

declare const dtiConfigQueryNameBrand: unique symbol;
export type DtiConfigQueryName = string & {
  readonly [dtiConfigQueryNameBrand]: 'DtiConfigQueryName';
};

declare const membershipPlanNameBrand: unique symbol;
export type MembershipPlanName = string & {
  readonly [membershipPlanNameBrand]: 'MembershipPlanName';
};

declare const billingFrequencyBrand: unique symbol;
export type BillingFrequency = string & {
  readonly [billingFrequencyBrand]: 'BillingFrequency';
};

declare const currencyCodeBrand: unique symbol;
export type CurrencyCode = string & {
  readonly [currencyCodeBrand]: 'CurrencyCode';
};

declare const promoCodeBrand: unique symbol;
export type PromoCode = string & { readonly [promoCodeBrand]: 'PromoCode' };

declare const fileAttachmentIdBrand: unique symbol;
export type FileAttachmentId = string & {
  readonly [fileAttachmentIdBrand]: 'FileAttachmentId';
};

// Domain Code Types (open branded strings, not closed enums)
declare const specialtyCodeBrand: unique symbol;
export type SpecialtyCode = string & {
  readonly [specialtyCodeBrand]: 'SpecialtyCode';
};

declare const counselingStyleCodeBrand: unique symbol;
export type CounselingStyleCode = string & {
  readonly [counselingStyleCodeBrand]: 'CounselingStyleCode';
};

declare const browserTimezoneBrand: unique symbol;
export type BrowserTimezone = string & {
  readonly [browserTimezoneBrand]: 'BrowserTimezone';
};

declare const otherCompanyNameBrand: unique symbol;
export type OtherCompanyName = string & {
  readonly [otherCompanyNameBrand]: 'OtherCompanyName';
};

// Display/Label Types
declare const providerDisplayNameBrand: unique symbol;
export type ProviderDisplayName = string & {
  readonly [providerDisplayNameBrand]: 'ProviderDisplayName';
};

declare const providerBioBrand: unique symbol;
export type ProviderBio = string & {
  readonly [providerBioBrand]: 'ProviderBio';
};

declare const insuranceCompanyNameBrand: unique symbol;
export type InsuranceCompanyName = string & {
  readonly [insuranceCompanyNameBrand]: 'InsuranceCompanyName';
};

declare const ageGroupCodeBrand: unique symbol;
export type AgeGroupCode = string & {
  readonly [ageGroupCodeBrand]: 'AgeGroupCode';
};

declare const ageGroupLabelBrand: unique symbol;
export type AgeGroupLabel = string & {
  readonly [ageGroupLabelBrand]: 'AgeGroupLabel';
};

declare const urlBrand: unique symbol;
export type Url = string & { readonly [urlBrand]: 'Url' };

// Status Types (open branded strings, not closed enums)
declare const leadStatusBrand: unique symbol;
export type LeadStatus = string & { readonly [leadStatusBrand]: 'LeadStatus' };

// Tracking Types
declare const sponsorCodeBrand: unique symbol;
export type SponsorCode = string & {
  readonly [sponsorCodeBrand]: 'SponsorCode';
};

declare const searchQueryBrand: unique symbol;
export type SearchQuery = string & {
  readonly [searchQueryBrand]: 'SearchQuery';
};

declare const rdSearchQueryBrand: unique symbol;
export type RdSearchQuery = string & {
  readonly [rdSearchQueryBrand]: 'RdSearchQuery';
};

declare const referralSourceValueBrand: unique symbol;
export type ReferralSourceValue = string & {
  readonly [referralSourceValueBrand]: 'ReferralSourceValue';
};

declare const referralSourceOtherBrand: unique symbol;
export type ReferralSourceOther = string & {
  readonly [referralSourceOtherBrand]: 'ReferralSourceOther';
};

declare const referringPhysicianBrand: unique symbol;
export type ReferringPhysician = string & {
  readonly [referringPhysicianBrand]: 'ReferringPhysician';
};

declare const referringPracticeBrand: unique symbol;
export type ReferringPractice = string & {
  readonly [referringPracticeBrand]: 'ReferringPractice';
};

declare const referrerNameBrand: unique symbol;
export type ReferrerName = string & {
  readonly [referrerNameBrand]: 'ReferrerName';
};

declare const referrerIdBrand: unique symbol;
export type ReferrerId = string & { readonly [referrerIdBrand]: 'ReferrerId' };

declare const referringSourceBrand: unique symbol;
export type ReferringSource = string & {
  readonly [referringSourceBrand]: 'ReferringSource';
};

// ============================================================================
// HELPER UTILITIES
// ============================================================================

/** Shared non-empty trimmed string validator. Use for identity/label fields. */
const nonEmptyTrimmed = z.string().trim().min(1);
const RFV_INGRESS_MAX_CODE_POINTS = 128;
export const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001F\u007F-\u009F]/;

function isWithinRfvIngressCodePointLimit(value: string): boolean {
  let codePointCount = 0;
  for (let offset = 0; offset < value.length; offset += 1) {
    const codePoint = value.codePointAt(offset);
    if (codePoint !== undefined && codePoint > 0xffff) {
      offset += 1;
    }
    codePointCount += 1;
    if (codePointCount > RFV_INGRESS_MAX_CODE_POINTS) {
      return false;
    }
  }
  return true;
}

function createExactEnumSchema<
  const TValues extends readonly [string, ...string[]],
>(values: TValues) {
  return z.string().trim().min(1).pipe(z.enum(values));
}

function createFallbackEnumSchema<
  const TValues extends readonly [string, ...string[]],
  const TFallback extends TValues[number] | null,
>(
  values: TValues,
  fallback: TFallback,
): z.ZodType<TValues[number] | TFallback> {
  const valueSet = new Set<string>(values);

  return z
    .string()
    .trim()
    .min(1)
    .transform((value): TValues[number] | TFallback =>
      valueSet.has(value) ? (value as TValues[number]) : fallback,
    );
}

/**
 * Wrap any schema to allow null.
 * Example: nullable(providerDisplayNameSchema) → ProviderDisplayName | null
 */
export function nullable<T extends z.ZodTypeAny>(
  schema: T,
): z.ZodType<z.infer<T> | null> {
  return z.union([schema, z.null()]);
}

function blankStringAsNull<T extends z.ZodTypeAny>(
  schema: T,
): z.ZodType<z.infer<T> | null> {
  return z.preprocess(
    (value) =>
      typeof value === 'string' && value.trim() === '' ? null : value,
    nullable(schema),
  );
}

/**
 * Parse a value using the given schema, returning Maybe<T>.
 * Use this in components/hooks where optional results make sense.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- {} is the canonical non-nullish constraint
export function parseBranded<T extends {}>(
  schema: z.ZodType<T>,
  value: unknown,
): Maybe<T> {
  const result = schema.safeParse(value);
  return result.success ? just(result.data) : nothing();
}

/**
 * Parse a value using the given schema, throwing on failure.
 * Use this in non-optional contexts (assertions, required operations).
 */
export function unsafeParse<T>(schema: z.ZodType<T>, value: unknown): T {
  return schema.parse(value);
}

// ============================================================================
// TIER 1 SCHEMAS (Structural Validation)
// ============================================================================

export const isoTimestampSchema = z
  .string()
  .trim()
  .min(1)
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})$/,
    'Invalid ISO timestamp format',
  )
  .refine((v) => !Number.isNaN(Date.parse(v)), { message: 'Invalid timestamp' })
  .transform((value): IsoTimestamp => value as IsoTimestamp);

export const normalizedStateCodeSchema: z.ZodType<NormalizedStateCode, string> =
  z
    .string()
    .trim()
    .toUpperCase()
    .pipe(
      z.enum(NORMALIZED_STATE_CODE_VALUES, {
        message: 'Must be a supported normalized state or territory code',
      }),
    )
    .transform((value): NormalizedStateCode => brandNormalizedStateCode(value));

export const usStateCodeSchema: z.ZodType<UsStateCode, string> = z
  .string()
  .trim()
  .toUpperCase()
  .pipe(
    z.enum(US_STATE_CODE_VALUES, {
      message: 'Must be a supported US state code',
    }),
  )
  .transform((value): UsStateCode => brandUsStateCode(value));

export const maskedEmailSchema = z
  .string()
  .trim()
  .regex(
    /^.+\*{3}@.+$/,
    'Must match masked email format: example***@domain.com',
  )
  .transform((value): MaskedEmail => value as MaskedEmail);

// CHAS: public_lead_type.rb:55-64 — "***-***-#{digits.last(4)}" when >= 4 digits
export const maskedPhoneSchema = z
  .string()
  .trim()
  .regex(/^\*{3}-\*{3}-\d{4}$/, 'Must match masked phone format: ***-***-XXXX')
  .transform((value): MaskedPhone => value as MaskedPhone);

export const urlSchema = z
  .string()
  .trim()
  .url('Must be a valid URL')
  .transform((value): Url => value as Url);

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const isoDateSchema = z
  .string()
  .trim()
  .regex(ISO_DATE_PATTERN, 'Must be in YYYY-MM-DD format')
  .refine(
    (v) => {
      const [year, month, day] = v.split('-').map(Number);
      if (year === undefined || month === undefined || day === undefined)
        return false;
      const date = new Date(Date.UTC(year, month - 1, day));
      return (
        !Number.isNaN(date.getTime()) &&
        date.getUTCFullYear() === year &&
        date.getUTCMonth() + 1 === month &&
        date.getUTCDate() === day
      );
    },
    { message: 'Must be a valid calendar date' },
  )
  .transform((value): IsoDate => value as IsoDate);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const emailSchema = z
  .string()
  .trim()
  .min(1, 'Must be a non-empty email address')
  .regex(EMAIL_PATTERN, 'Must be a valid email address')
  .transform((value): Email => value as Email);

export const firstNameSchema = nonEmptyTrimmed.transform(
  (value): FirstName => value as FirstName,
);

export const lastNameSchema = nonEmptyTrimmed.transform(
  (value): LastName => value as LastName,
);

const E164_PATTERN = /^\+[1-9]\d{1,14}$/;

function toE164(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10 && digits[0] !== '0') return `+1${digits}`;
  if (digits.length === 11 && digits[0] === '1' && digits[1] !== '0')
    return `+${digits}`;
  return raw;
}

export const usPhoneSchema = z
  .string()
  .trim()
  .refine(
    (raw) => E164_PATTERN.test(toE164(raw)),
    'Must be a valid US phone number',
  )
  .transform((raw): UsPhone => toE164(raw) as UsPhone);

export const zipCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{5}$/, 'Must be a 5-digit US zip code')
  .transform((value): ZipCode => value as ZipCode);

// ============================================================================
// TIER 2 SCHEMAS (Enum Validation)
// ============================================================================

export const BOOKING_ERROR_CODE_VALUES = [
  'SLOT_UNAVAILABLE',
  'PROVIDER_UNAVAILABLE',
  'SCHEDULING_TIMEOUT',
  'PAYMENT_FAILED',
  'VALIDATION_FAILED',
  'RECONCILIATION_FAILED',
  'ALREADY_BOOKED',
  'INITIAL_ALREADY_BOOKED',
  'BOOKING_IN_PROGRESS',
  'RATE_LIMITED',
  'INTERNAL_ERROR',
  'UNKNOWN',
] as const;

export type BookingErrorCode = (typeof BOOKING_ERROR_CODE_VALUES)[number];

export const bookingErrorCodeExactSchema = createExactEnumSchema(
  BOOKING_ERROR_CODE_VALUES,
);

export const bookingErrorCodeSchema: z.ZodType<BookingErrorCode> =
  createFallbackEnumSchema(BOOKING_ERROR_CODE_VALUES, 'UNKNOWN');

export const INSURANCE_STATUS_VALUES = [
  'IN_NETWORK',
  'OUT_OF_NETWORK',
  'SELF_PAY',
  'UNKNOWN',
] as const;

export type InsuranceStatus = (typeof INSURANCE_STATUS_VALUES)[number];

export const insuranceStatusExactSchema = createExactEnumSchema(
  INSURANCE_STATUS_VALUES,
);

export const insuranceStatusSchema: z.ZodType<InsuranceStatus> =
  createFallbackEnumSchema(INSURANCE_STATUS_VALUES, 'UNKNOWN');

export const ELIGIBILITY_STATUS_VALUES = [
  'ACTIVE',
  'INACTIVE',
  'NOT_FOUND',
  'UNKNOWN',
  'API_ERROR',
  'TIMEOUT',
  'PENDING',
] as const;

export type EligibilityStatus = (typeof ELIGIBILITY_STATUS_VALUES)[number];

export const eligibilityStatusExactSchema = createExactEnumSchema(
  ELIGIBILITY_STATUS_VALUES,
);

export const eligibilityStatusSchema: z.ZodType<EligibilityStatus> =
  createFallbackEnumSchema(ELIGIBILITY_STATUS_VALUES, 'UNKNOWN');

export const BOOKING_SOURCE_VALUES = ['BEST_MATCH', 'PROVIDER_LIST'] as const;

export const bookingSourceSchema = z.enum(BOOKING_SOURCE_VALUES);
export type BookingSource = z.infer<typeof bookingSourceSchema>;
export const MAGIC_LINK_REASON_VALUES = [
  'IDENTITY_CHECK',
  'EMAIL_CHANGE_CONFIRMATION',
] as const;

export const magicLinkReasonSchema = z.enum(MAGIC_LINK_REASON_VALUES);
export type MagicLinkReason = z.infer<typeof magicLinkReasonSchema>;

export const PUBLIC_LEAD_BOOKING_STATUS_VALUES = [
  'NOT_BOOKED',
  'IN_PROGRESS',
  'CONFIRMED',
] as const;

export type PublicLeadBookingStatus =
  (typeof PUBLIC_LEAD_BOOKING_STATUS_VALUES)[number];

export const REASONS_FOR_VISIT_CODE_VALUES = [
  'RFV_ANY',
  'RFV_SUSTAINABLE_WEIGHT_LOSS',
  'RFV_HEART_HEALTH',
  'RFV_PREDIABETES',
  'RFV_TYPE_2_DIABETES',
  'RFV_GASTROINTESTINAL_HEALTH',
  'RFV_OTHER',
  'RFV_GENERAL_HEALTHY_EATING',
  'RFV_EATING_DISORDERS',
  'RFV_RELATIONSHIP_WITH_FOOD',
  'RFV_TYPE_1_DIABETES',
  'RFV_GESTATIONAL_DIABETES',
  'RFV_HORMONE_HEALTH',
  'RFV_FERTILITY_NUTRITION',
  'RFV_CANCER_CARE_NUTRITION',
  'RFV_SPORTS_NUTRITION',
  'RFV_FOOD_ALLERGIES',
  'RFV_THYROID_CONDITION',
  'RFV_PEDIATRICS',
  'RFV_KIDNEY_LIVER_HEALTH',
  'RFV_WEIGHT_MANAGEMENT',
  'RFV_GLP1_CARE',
] as const;

export const reasonForVisitCodeExactSchema: z.ZodType<
  ReasonForVisitCode,
  string
> = createExactEnumSchema(REASONS_FOR_VISIT_CODE_VALUES).transform(
  (value): ReasonForVisitCode => value as ReasonForVisitCode,
);

// Enum: backed by CHAS SpecialtyEnum
export const specialtyCodeValueSchema = z.enum(SPECIALTY_CODE_VALUES);

// Enum: backed by CHAS AvailabilityErrorCodeEnum (availability_error_code_enum.rb)
const KNOWN_PROVIDER_AVAILABILITY_ERROR_CODE_VALUES = [
  'PROVIDER_NOT_FOUND',
  'PROVIDER_API_TIMEOUT',
  'PROVIDER_API_ERROR',
] as const;
const PROVIDER_AVAILABILITY_ERROR_CODE_VALUES = [
  ...KNOWN_PROVIDER_AVAILABILITY_ERROR_CODE_VALUES,
  'UNKNOWN',
] as const;

export const providerAvailabilityErrorCodeExactSchema = createExactEnumSchema(
  KNOWN_PROVIDER_AVAILABILITY_ERROR_CODE_VALUES,
);
export const providerAvailabilityErrorCodeSchema = createFallbackEnumSchema(
  PROVIDER_AVAILABILITY_ERROR_CODE_VALUES,
  'UNKNOWN',
);
export type ProviderAvailabilityErrorCode = z.infer<
  typeof providerAvailabilityErrorCodeSchema
>;

// Enum: backed by CHAS AvailabilityFetchStatusEnum (availability_fetch_status_enum.rb)
const KNOWN_AVAILABILITY_FETCH_STATUS_VALUES = ['SUCCESS', 'FAILED'] as const;
const AVAILABILITY_FETCH_STATUS_VALUES = [
  ...KNOWN_AVAILABILITY_FETCH_STATUS_VALUES,
  'UNKNOWN',
] as const;

export const availabilityFetchStatusExactSchema = createExactEnumSchema(
  KNOWN_AVAILABILITY_FETCH_STATUS_VALUES,
);
export const availabilityFetchStatusSchema = createFallbackEnumSchema(
  AVAILABILITY_FETCH_STATUS_VALUES,
  'UNKNOWN',
);
export type AvailabilityFetchStatus = z.infer<
  typeof availabilityFetchStatusSchema
>;

// TIER 3 SCHEMAS (Identity/Non-empty Trimmed)
// ============================================================================
//
// These schemas are intentionally nominal-only. They guarantee the value is a
// non-empty trimmed string at the boundary, then apply the brand so callers
// cannot mix semantically distinct identifiers by accident. They do not assert
// backend-specific ID formats unless the transport contract actually guarantees
// one.

export const appointmentIdSchema = nonEmptyTrimmed.transform(
  (value): AppointmentId => value as AppointmentId,
);

export const memberIdSchema = nonEmptyTrimmed.transform(
  (value): MemberId => value as MemberId,
);

export const groupIdSchema = nonEmptyTrimmed.transform(
  (value): GroupId => value as GroupId,
);

export const providerIdSchema = nonEmptyTrimmed.transform(
  (value): ProviderId => value as ProviderId,
);

export const leadIdSchema = nonEmptyTrimmed.transform(
  (value): LeadId => value as LeadId,
);

export const insuranceCompanyIdSchema = nonEmptyTrimmed
  .refine((value) => !isReservedInsuranceSelectionValue(value), {
    message: 'Must be an insurance company id, not a reserved selection value',
  })
  .transform((value): InsuranceCompanyId => value as InsuranceCompanyId);

export const insuranceSearchCodeSchema = nonEmptyTrimmed.transform(
  (value): InsuranceSearchCode => value as InsuranceSearchCode,
);

export const reasonForVisitCodeSchema = nonEmptyTrimmed.transform(
  (value): ReasonForVisitCode => value as ReasonForVisitCode,
);

/**
 * Frontend-owned hygiene for URL and session-storage ingress only.
 * CHAS owns the open RFV vocabulary, so this deliberately asserts no grammar
 * or membership and must not replace reasonForVisitCodeSchema on responses.
 */
export const reasonForVisitCodeIngressSchema: z.ZodType<
  ReasonForVisitCode,
  string
> = z
  .string()
  .refine((value) => !CONTROL_CHARACTER_PATTERN.test(value), {
    message: 'Reason-for-visit codes cannot contain control characters',
  })
  .pipe(reasonForVisitCodeSchema)
  .refine(isWithinRfvIngressCodePointLimit, {
    message: `Reason-for-visit codes cannot exceed ${RFV_INGRESS_MAX_CODE_POINTS} code points`,
  });

export const stripeTokenSchema = nonEmptyTrimmed.transform(
  (value): StripeToken => value as StripeToken,
);

// Coupled to CHAS issuance: LeadInvite `has_secure_token :token` (Rails default
// 24-char base58) and Rails.application.message_verifier
// ("<strict padded base64>--<40-char lowercase sha1 hex>").
// Mangled links fail here, so /book/resume never round-trips them to CHAS.
export const LEAD_INVITE_TOKEN_LENGTH = 24;
/** Bitcoin base58 range body (no 0, O, I, l), used inside `[...]`. */
export const LEAD_INVITE_TOKEN_CHARS = '1-9A-HJ-NP-Za-km-z';
export const MAGIC_LINK_DIGEST_HEX_LENGTH = 40;

const LEAD_INVITE_TOKEN_PATTERN = new RegExp(
  `^[${LEAD_INVITE_TOKEN_CHARS}]{${LEAD_INVITE_TOKEN_LENGTH}}$`,
);
// Strict RFC 4648 Base64: canonical padding, and the unused bits of the final
// sextet must be zero ([AQgw] before "==", [AEIMQUYcgkosw048] before "="),
// matching Ruby's strict decoder.
const STRICT_BASE64_PATTERN =
  '(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/][AQgw]==|[A-Za-z0-9+/]{2}[AEIMQUYcgkosw048]=|[A-Za-z0-9+/]{4})';
const MAGIC_LINK_TOKEN_PATTERN = new RegExp(
  `^${STRICT_BASE64_PATTERN}--[0-9a-f]{${MAGIC_LINK_DIGEST_HEX_LENGTH}}$`,
);

export const magicLinkTokenSchema = z
  .string()
  .trim()
  .regex(MAGIC_LINK_TOKEN_PATTERN)
  .transform((value): MagicLinkToken => value as MagicLinkToken);

export const leadInviteTokenSchema = z
  .string()
  .trim()
  .regex(LEAD_INVITE_TOKEN_PATTERN)
  .transform((value): LeadInviteToken => value as LeadInviteToken);

export const dtiSessionTokenSchema = nonEmptyTrimmed.transform(
  (value): DtiSessionToken => value as DtiSessionToken,
);

export const membershipPackageIdSchema = nonEmptyTrimmed.transform(
  (value): MembershipPackageId => value as MembershipPackageId,
);

export const membershipPackageNameSchema = nonEmptyTrimmed.transform(
  (value): MembershipPackageName => value as MembershipPackageName,
);

export const membershipPackageDescriptionSchema = nonEmptyTrimmed.transform(
  (value): MembershipPackageDescription =>
    value as MembershipPackageDescription,
);

export const membershipIntervalSchema = nonEmptyTrimmed.transform(
  (value): MembershipInterval => value as MembershipInterval,
);

export const partnerNameSchema = nonEmptyTrimmed.transform(
  (value): PartnerName => value as PartnerName,
);

export const dtiConfigConditionFieldSchema = nonEmptyTrimmed.transform(
  (value): DtiConfigConditionField => value as DtiConfigConditionField,
);

export const dtiConfigConditionValueSchema = nonEmptyTrimmed.transform(
  (value): DtiConfigConditionValue => value as DtiConfigConditionValue,
);

export const dtiConfigActionNameSchema = nonEmptyTrimmed.transform(
  (value): DtiConfigActionName => value as DtiConfigActionName,
);

export const dtiConfigFieldNameSchema = nonEmptyTrimmed.transform(
  (value): DtiConfigFieldName => value as DtiConfigFieldName,
);

export const dtiConfigStepNameSchema = nonEmptyTrimmed.transform(
  (value): DtiConfigStepName => value as DtiConfigStepName,
);

export const dtiConfigQueryNameSchema = nonEmptyTrimmed.transform(
  (value): DtiConfigQueryName => value as DtiConfigQueryName,
);

export const membershipPlanNameSchema = nonEmptyTrimmed.transform(
  (value): MembershipPlanName => value as MembershipPlanName,
);

export const billingFrequencySchema = nonEmptyTrimmed.transform(
  (value): BillingFrequency => value as BillingFrequency,
);

export const currencyCodeSchema = nonEmptyTrimmed.transform(
  (value): CurrencyCode => value as CurrencyCode,
);

export const promoCodeSchema = nonEmptyTrimmed.transform(
  (value): PromoCode => value as PromoCode,
);

export const fileAttachmentIdSchema = nonEmptyTrimmed.transform(
  (value): FileAttachmentId => value as FileAttachmentId,
);

export const specialtyCodeSchema = nonEmptyTrimmed.transform(
  (value): SpecialtyCode => value as SpecialtyCode,
);

export const counselingStyleCodeSchema = nonEmptyTrimmed.transform(
  (value): CounselingStyleCode => value as CounselingStyleCode,
);
export const AGE_GROUP_CODE_VALUES = [
  'AG_YOUNG_CHILDREN',
  'AG_ADOLESCENT',
  'AG_EARLY_TEEN',
  'AG_LATE_TEEN',
  'AG_ADULT',
] as const;

export const ageGroupCodeExactSchema = createExactEnumSchema(
  AGE_GROUP_CODE_VALUES,
).transform((value): AgeGroupCode => value as AgeGroupCode);

// NOTE: browserTimezoneSchema intentionally omitted — the BrowserTimezone
// brand carries an IANA-validity witness minted by asBrowserTimezone /
// getBrowserTimezone in src/core/browserTimezone.ts. Routing zones
// through a Zod transform without the Intl probe would re-introduce the
// brand-forgery hole the validators closed.

export const otherCompanyNameSchema = nonEmptyTrimmed.transform(
  (value): OtherCompanyName => value as OtherCompanyName,
);

// Display/Label schemas
export const providerDisplayNameSchema = nonEmptyTrimmed.transform(
  (value): ProviderDisplayName => value as ProviderDisplayName,
);

export const providerBioSchema = nonEmptyTrimmed.transform(
  (value): ProviderBio => value as ProviderBio,
);

export const insuranceCompanyNameSchema = nonEmptyTrimmed.transform(
  (value): InsuranceCompanyName => value as InsuranceCompanyName,
);

export const ageGroupCodeSchema = nonEmptyTrimmed.transform(
  (value): AgeGroupCode => value as AgeGroupCode,
);

export const ageGroupLabelSchema = nonEmptyTrimmed.transform(
  (value): AgeGroupLabel => value as AgeGroupLabel,
);

// Partner-config age-group gate: 'adult' | 'pediatric'.
// Distinct from search-side AgeGroupCode (AG_* enum) by design: CHAS documents
// this field as copy-only; it must never serialize into searchProviders(ageGroup:).
// A dedicated literal-union schema (not a branded string) makes cross-assignment
// a compile error instead of GraphQL enum-coercion failure.
// See docs/adr/0006-partner-age-group-vocabulary.md.
export const partnerAgeGroupSchema = z.enum(['adult', 'pediatric']);
export type PartnerAgeGroup = z.infer<typeof partnerAgeGroupSchema>;

// Status/Open string schemas
export const leadStatusSchema = nonEmptyTrimmed.transform(
  (value): LeadStatus => value as LeadStatus,
);

// Tracking schemas
export const sponsorCodeSchema = nonEmptyTrimmed.transform(
  (value): SponsorCode => value as SponsorCode,
);

export const searchQuerySchema = nonEmptyTrimmed.transform(
  (value): SearchQuery => value as SearchQuery,
);

export const rdSearchQuerySchema = nonEmptyTrimmed.transform(
  (value): RdSearchQuery => value as RdSearchQuery,
);

export const referralSourceValueSchema = nonEmptyTrimmed.transform(
  (value): ReferralSourceValue => value as ReferralSourceValue,
);

export const referralSourceOtherSchema = nonEmptyTrimmed.transform(
  (value): ReferralSourceOther => value as ReferralSourceOther,
);

export const referringPhysicianSchema = nonEmptyTrimmed.transform(
  (value): ReferringPhysician => value as ReferringPhysician,
);

export const referringPracticeSchema = nonEmptyTrimmed.transform(
  (value): ReferringPractice => value as ReferringPractice,
);

export const referrerNameSchema = nonEmptyTrimmed.transform(
  (value): ReferrerName => value as ReferrerName,
);

export const referrerIdSchema = nonEmptyTrimmed.transform(
  (value): ReferrerId => value as ReferrerId,
);

export const referringSourceSchema = nonEmptyTrimmed.transform(
  (value): ReferringSource => value as ReferringSource,
);

// ============================================================================
// RESPONSE SCHEMAS
// ============================================================================

// Public Specialty — code is an open string at the transport boundary because
// CHAS exposes code as String (not an enum). Closed-vocabulary filtering
// happens in the UI/route layer via specialtyCodeValueSchema.
export const publicSpecialtySchema = z.object({
  code: specialtyCodeSchema,
  name: nullable(nonEmptyTrimmed),
});

// Public Counseling Style — same open-string rationale as specialties.
export const publicCounselingStyleSchema = z.object({
  code: counselingStyleCodeSchema,
  name: nonEmptyTrimmed,
});

// Public Age Group
export const publicAgeGroupSchema = z.object({
  code: ageGroupCodeSchema,
  name: ageGroupLabelSchema,
  label: ageGroupLabelSchema,
});

// Availability
export const availabilitySchema = z.object({
  startTime: isoTimestampSchema,
});

export const providerAvailabilityPreviewSchema = z.object({
  slots: z.array(availabilitySchema),
});

// Provider Summary
export const providerSummarySchema = z.object({
  id: providerIdSchema,
  firstName: nullable(providerDisplayNameSchema),
  lastName: nullable(providerDisplayNameSchema),
  // CHAS sends "" for providers without a bio; treat it as absent.
  bio: blankStringAsNull(providerBioSchema),
  photoUrl: nullable(urlSchema),
  states: nullable(z.array(normalizedStateCodeSchema)).transform(
    (value) => value ?? [],
  ),
  reasonsForVisit: nullable(z.array(reasonForVisitCodeSchema)).transform(
    (value) => value ?? [],
  ),
  nextAvailableSlot: nullable(isoTimestampSchema),
  nextAvailableDate: isoDateSchema.nullable(),
  availabilityPreview: providerAvailabilityPreviewSchema
    .nullable()
    .transform((value) => value ?? null),
  specialties: z.array(publicSpecialtySchema),
  counselingStyles: z.array(publicCounselingStyleSchema),
  ageGroups: z.array(publicAgeGroupSchema),
  acceptingNewPatients: z.boolean(),
});

// Insurance Company
export const insuranceCompanySchema = z.object({
  id: insuranceCompanyIdSchema,
  name: insuranceCompanyNameSchema,
  insuranceSearchCode: insuranceSearchCodeSchema,
});

export const publicLeadHeightWeightSchema = z.object({
  heightFeet: z.number().int().nullable(),
  heightInches: z.number().int().nullable(),
  weightLbs: z.number().int().nullable(),
});

export const publicLeadInsuranceSchema = z.object({
  selection: z
    .object({
      type: z.enum(['SPONSOR', 'SELF_PAY', 'OTHER']),
      networkStatus: insuranceStatusSchema,
      sponsorId: insuranceCompanyIdSchema.nullable(),
      otherCompanyName: otherCompanyNameSchema.nullable(),
    })
    .nullable(),
  details: z
    .object({
      memberIdOnFile: z.boolean(),
      groupNumberOnFile: z.boolean(),
      subscriberRelationship: z.enum(SUBSCRIBER_RELATIONSHIPS).nullable(),
      policyholderOnFile: z.boolean(),
    })
    .nullable(),
  eligibilityStatus: eligibilityStatusSchema.nullable(),
  cards: z
    .object({
      front: z.object({ uploaded: z.boolean() }),
      back: z.object({ uploaded: z.boolean() }),
    })
    .nullable(),
  companyName: insuranceCompanyNameSchema.nullable(),
});

export const publicLeadAppointmentSchema = z.object({
  id: appointmentIdSchema.nullable(),
  providerId: providerIdSchema,
  providerName: providerDisplayNameSchema,
  providerPhotoUrl: urlSchema.nullable(),
  startTime: isoTimestampSchema,
  endTime: isoTimestampSchema,
  patientTimezone: nonEmptyTrimmed,
  joinUrl: urlSchema.nullable(),
});

export const publicLeadPaymentSchema = z.object({
  selectedMembershipPlanId: membershipPackageIdSchema.nullable(),
  cardOnFile: z.boolean(),
  paid: z.boolean(),
});

// Public Lead
export const publicLeadSchema = z.object({
  firstName: firstNameSchema.nullable(),
  lastName: lastNameSchema.nullable(),
  email: maskedEmailSchema.nullable(),
  phone: maskedPhoneSchema.nullable(),
  stateCode: usStateCodeSchema.nullable(),
  reasonForVisitCode: reasonForVisitCodeSchema.nullable(),
  sponsorCode: sponsorCodeSchema.nullable(),
  emailReadOnly: z.boolean().default(false),
  dateOfBirthOnFile: z.boolean(),
  referralSourceOnFile: z.boolean(),
  consentToContactViaSms: z.boolean().nullable().default(null),
  appointmentConsent: z.boolean().nullable().default(null),
  heightWeight: publicLeadHeightWeightSchema.nullable(),
  insurance: publicLeadInsuranceSchema.nullable(),
  appointment: publicLeadAppointmentSchema.nullable(),
  payment: publicLeadPaymentSchema.nullable(),
  bookingStatus: createExactEnumSchema(PUBLIC_LEAD_BOOKING_STATUS_VALUES),
});

// Insurance Status Result
export const insuranceStatusResultSchema = z.object({
  status: insuranceStatusSchema,
  insuranceCompanyName: nullable(insuranceCompanyNameSchema),
});

export const bookingPaymentSchema = z.object({
  paid: z.boolean(),
  cardOnFile: z.boolean(),
});

export const selectedMembershipPlanPaymentSchema = z.object({
  selectedMembershipPlanId: membershipPackageIdSchema.nullable(),
});

// Field Error
export const fieldErrorSchema = z.object({
  field: z.array(z.string()),
  messages: z.array(z.string()),
  code: z
    .string()
    .nullish()
    .transform((value): 'AGE_INELIGIBLE' | null =>
      value === 'AGE_INELIGIBLE' ? value : null,
    ),
});

// ============================================================================
// GRAPHQL RESPONSE SCHEMAS (Operation Results)
// ============================================================================

/**
 * Insurance Companies Query Response
 * Returns array of insurance companies with ID and name
 */
export const insuranceCompaniesResponseSchema = z.object({
  insuranceCompanies: z.array(insuranceCompanySchema),
});

/**
 * Search Providers Query Response
 * Returns paginated list of providers matching search criteria
 */
export const searchProvidersResponseSchema = z.object({
  searchProviders: z.object({
    providers: z.array(providerSummarySchema),
    total: z.number(),
    page: z.number(),
    perPage: z.number(),
    broadened: z.boolean(),
    availabilityFetchStatus: availabilityFetchStatusSchema,
    earliestAvailableSlot: isoTimestampSchema.nullable().optional(),
    availabilityWindowStart: isoTimestampSchema.nullable().optional(),
    availabilityWindowEnd: isoTimestampSchema.nullable().optional(),
    nextAvailabilityWindowStart: isoTimestampSchema.nullable().optional(),
    hasNextAvailabilityWindow: z.boolean().nullable().optional(),
  }),
});

/**
 * Provider Availability Query Response (Raw transport)
 * Returns availability slots for a specific provider
 * Note: The raw response includes errorCode which is classified into
 * ProviderAvailabilityResult by separate logic after parsing.
 */
export const providerAvailabilityResponseSchema = z.object({
  providerAvailability: z.object({
    providerId: providerIdSchema,
    slots: z.array(availabilitySchema),
    intervalStart: nullable(isoTimestampSchema),
    intervalEnd: nullable(isoTimestampSchema),
    nextIntervalStart: nullable(isoTimestampSchema),
    hasNextInterval: z.boolean(),
    errorCode: providerAvailabilityErrorCodeSchema.nullable(),
  }),
});

/**
 * Create Lead Mutation Response
 * Returns either created lead or magic link sent or validation errors
 * Lead classification happens separately after parsing
 */
export const createLeadResponseSchema = z.object({
  createLead: z.object({
    lead: publicLeadSchema.nullable(),
    status: z.enum(['OK', 'MAGIC_LINK_SENT', 'ALREADY_BOOKED', 'FAILED']),
    magicLinkReason: magicLinkReasonSchema
      .nullable()
      .optional()
      .transform((val) => val ?? null),
    errors: z.array(fieldErrorSchema),
  }),
});

export const publicLeadResponseSchema = z.object({
  publicLead: publicLeadSchema.nullable(),
});

/**
 * Update Lead Mutation Response
 * Returns either success with a public lead marker or validation errors
 * Result classification happens separately after parsing
 */
export const updateLeadResponseSchema = z.object({
  updateLead: z.object({
    lead: publicLeadSchema.nullable(),
    status: z.enum(['OK', 'MAGIC_LINK_SENT', 'FAILED']),
    magicLinkReason: magicLinkReasonSchema
      .nullable()
      .optional()
      .transform((val) => val ?? null),
    errors: z.array(fieldErrorSchema),
  }),
});

/**
 * Submit Insurance Mutation Response
 * Returns insurance status, eligibility status, and details or validation errors
 * Result classification happens separately after parsing
 */
export const submitInsuranceResponseSchema = z.object({
  submitInsurance: z.object({
    lead: publicLeadSchema.nullable(),
    errors: z.array(fieldErrorSchema),
  }),
});

/**
 * Book Appointment Mutation Response
 * Returns appointment details or error information with error classification
 * Result classification (booked, already_booked, retryable_failure, validation_error, fatal_failure)
 * happens separately after parsing based on appointment presence and error types
 */
export const bookAppointmentResponseSchema = z.object({
  bookAppointment: z.object({
    lead: publicLeadSchema.nullable(),
    errorCode: bookingErrorCodeSchema.nullable(),
    retryable: z.boolean(),
    errors: z.array(fieldErrorSchema),
  }),
});

export const dtiConfigVariantSchema = z.enum(['DEFAULT']);

export const uploadInsuranceCardSideSchema = z.enum(['FRONT', 'BACK']);

export const publicReasonForVisitSchema = z.object({
  code: reasonForVisitCodeSchema,
  name: nonEmptyTrimmed,
});

export const membershipPackageSchema = z.object({
  id: membershipPackageIdSchema,
  name: membershipPackageNameSchema,
  priceCents: z.number().int().nullable(),
  description: blankStringAsNull(membershipPackageDescriptionSchema),
  interval: nullable(membershipIntervalSchema),
});

const partnerEligibleInsurerSchema = insuranceCompanySchema.pick({
  id: true,
  name: true,
});

export const partnerConfigSchema = z.object({
  name: partnerNameSchema,
  code: sponsorCodeSchema,
  brandLogoUrl: urlSchema
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  creditCardRequired: z.boolean(),
  insuranceRequired: z.boolean(),
  codeEntryEnabled: z.boolean(),
  ageGroup: partnerAgeGroupSchema,
  restrictInsurers: z.boolean(),
  eligibleInsurers: z.array(partnerEligibleInsurerSchema),
});

export const dtiConfigStepConditionSchema = z.object({
  field: dtiConfigConditionFieldSchema,
  values: z.array(dtiConfigConditionValueSchema),
});

export const dtiConfigActionSchema = z.object({
  name: dtiConfigActionNameSchema,
  fields: z.array(dtiConfigFieldNameSchema),
});

export const dtiConfigFieldSchema = z.object({
  name: dtiConfigFieldNameSchema,
  required: z.boolean(),
  order: z.number(),
});

export const dtiConfigStepSchema = z.object({
  name: dtiConfigStepNameSchema,
  order: z.number(),
  required: z.boolean(),
  condition: dtiConfigStepConditionSchema.nullable(),
  queries: z.array(dtiConfigQueryNameSchema),
  actions: z.array(dtiConfigActionSchema),
  fields: z.array(dtiConfigFieldSchema),
});

export const searchProviderFiltersResponseSchema = z.object({
  searchProviderFilters: z.object({
    ageGroups: z.array(publicAgeGroupSchema),
    counselingStyles: z.array(publicCounselingStyleSchema),
    reasonsForVisit: z.array(publicReasonForVisitSchema),
  }),
});

export const membershipPackagesResponseSchema = z.object({
  membershipPackages: z.array(membershipPackageSchema),
});

export const referenceDataResponseSchema = z.object({
  insuranceCompanies: z.array(insuranceCompanySchema),
  reasonsForVisit: z.array(publicReasonForVisitSchema),
  membershipPackages: z.array(membershipPackageSchema),
});

export const partnerConfigResponseSchema = z.object({
  partnerConfig: partnerConfigSchema.nullable(),
});

export const dtiConfigResponseSchema = z.object({
  dtiConfig: z
    .object({
      variant: dtiConfigVariantSchema,
      steps: z.array(dtiConfigStepSchema),
    })
    .nullable(),
});

export const consumeMagicLinkResponseSchema = z.object({
  consumeMagicLink: z.object({
    lead: publicLeadSchema.nullable(),
    status: z.enum(['OK', 'ALREADY_BOOKED', 'INVALID_TOKEN', 'FAILED']),
    errors: z.array(fieldErrorSchema),
  }),
});

export const consumeLeadInviteResponseSchema = z.object({
  consumeLeadInvite: z.object({
    lead: publicLeadSchema.nullable(),
    status: z.enum(['OK', 'ALREADY_BOOKED', 'INVALID_TOKEN', 'FAILED']),
    errors: z.array(fieldErrorSchema),
  }),
});

export const requestMagicLinkResponseSchema = z.object({
  requestMagicLink: z.object({
    ok: z.boolean(),
    errors: z.array(fieldErrorSchema),
  }),
});

export const selectMembershipPlanResponseSchema = z.object({
  selectMembershipPlan: z.object({
    lead: publicLeadSchema.nullable(),
    errors: z.array(fieldErrorSchema),
  }),
});

export const savePaymentMethodResponseSchema = z.object({
  savePaymentMethod: z.object({
    lead: publicLeadSchema.nullable(),
    errors: z.array(fieldErrorSchema),
  }),
});

export const validatePromoCodeResponseSchema = z.object({
  validatePromoCode: z.object({
    valid: z.boolean(),
    discountedPriceCents: z.number().int().nonnegative().nullable(),
    newRecurringPriceCents: z.number().int().nonnegative().nullable(),
    repeatTimes: z.number().int().nullable(),
    couponCode: promoCodeSchema.nullable(),
    stillRequiresCreditCard: z.boolean().nullable(),
    errors: z.array(fieldErrorSchema),
  }),
});

export const uploadInsuranceCardResponseSchema = z.object({
  uploadInsuranceCard: z.object({
    lead: publicLeadSchema.nullable(),
    success: z.boolean(),
    fileAttachmentId: fileAttachmentIdSchema.nullable(),
    errors: z.array(fieldErrorSchema),
  }),
});
